import { ScrollTrigger } from "gsap/ScrollTrigger";
import { gsap } from "gsap";


const animations = () => {

  const CONFIG = {
    duration: 0.85,
    ease:     'power3.out',
    stagger:  0.08,
    start:    'top 88%',
    once:     true,
    distance: 40,
    clipUp: {
      default: { ease: 'power4.out',                    duration: 1.4, stagger: 0.09, y: '1.3em' },
      soft:    { ease: 'cubic-bezier(0.25, 1, 0.5, 1)', duration: 1.2, stagger: 0,    y: '1.3em' },
      snappy:  { ease: 'expo.out',                      duration: 0.8, stagger: 0.05, y: '1em'   },
    },
  };

  // ── MAPPA CLASSI → DATA ATTRIBUTES ──────────────────────
  // Sistema alternativo ai data-animate hardcoded nel Twig.
  // Il cliente aggiunge classi da Gutenberg → Avanzate → Classi CSS.
  // Il JS le legge e imposta i data attributes corrispondenti
  // prima che il sistema principale le processi.
  //
  // Classi disponibili:
  //   data-animate-clipup       → data-animate="clip-up"
  //   data-animate-fade         → data-animate="fade"
  //   data-animate-slideup      → data-animate="slide-up"
  //   data-animate-slideleft    → data-animate="slide-left"
  //   data-animate-slideright   → data-animate="slide-right"
  //   data-animate-scale        → data-animate="scale"
  //   data-style-soft           → data-style="soft"
  //   data-style-default        → data-style="default"
  //   data-style-snappy         → data-style="snappy"
  //   data-duration-06          → data-duration="0.6"
  //   data-duration-1           → data-duration="1"
  //   data-duration-15          → data-duration="1.5"
  //   data-delay-08             → data-delay="0.8"  (nota: "08" → 0.8)
  //   data-delay-1              → data-delay="1"
  //   data-delay-15             → data-delay="1.5"

  const CLASS_MAP = {
    // Tipo animazione
    'data-animate-clipup':     { attr: 'animate',  val: 'clip-up'    },
    'data-animate-fade':       { attr: 'animate',  val: 'fade'       },
    'data-animate-slideup':    { attr: 'animate',  val: 'slide-up'   },
    'data-animate-slideleft':  { attr: 'animate',  val: 'slide-left' },
    'data-animate-slideright': { attr: 'animate',  val: 'slide-right'},
    'data-animate-scale':      { attr: 'animate',  val: 'scale'      },

    // Stile clip-up
    'data-style-soft':         { attr: 'style',    val: 'soft'       },
    'data-style-default':      { attr: 'style',    val: 'default'    },
    'data-style-snappy':       { attr: 'style',    val: 'snappy'     },

    // Durata — 3 valori
    'data-duration-06':        { attr: 'duration', val: '0.6'        },
    'data-duration-1':         { attr: 'duration', val: '1'          },
    'data-duration-15':        { attr: 'duration', val: '1.5'        },

    // Delay — 3 valori
    'data-delay-08':           { attr: 'delay',    val: '0.8'        },
    'data-delay-1':            { attr: 'delay',    val: '1'          },
    'data-delay-15':           { attr: 'delay',    val: '1.5'        },
  };

  // Traduce le classi in data attributes sul singolo elemento
  function applyClassMap(el) {
    let hasAnimateClass = false;

    Object.entries(CLASS_MAP).forEach(([cls, { attr, val }]) => {
      if (el.classList.contains(cls)) {
        el.dataset[attr] = val;
        if (attr === 'animate') hasAnimateClass = true;
      }
    });

    return hasAnimateClass;
  }

  // Cerca tutti gli elementi con classi data-animate-* nel DOM
  // e imposta i data attributes prima che il sistema li processi
  function resolveClassAnimations() {
    const selector = Object.keys(CLASS_MAP)
      .map(cls => `.${cls}`)
      .join(', ');

    document.querySelectorAll(selector).forEach(el => {
      // Salta se ha già data-animate hardcoded — non sovrascrivere
      if (el.dataset.animate) return;
      applyClassMap(el);
    });
  }

  // ── HELPERS ──────────────────────────────────────────────

  function opt(el, key, fallback) {
    const val = el.dataset[key];
    if (val === undefined || val === '') return fallback;
    if (val === 'true')  return true;
    if (val === 'false') return false;
    const num = parseFloat(val);
    return isNaN(num) ? val : num;
  }

  function wrapForClip(el) {
    if (el.querySelector('.anim-clip-inner')) return el.querySelector('.anim-clip-inner');
    el.style.overflow = 'hidden';
    el.style.display  = el.style.display || 'block';
    const inner         = document.createElement('span');
    inner.className     = 'anim-clip-inner';
    inner.style.display = 'block';
    inner.innerHTML     = el.innerHTML;
    el.innerHTML        = '';
    el.appendChild(inner);
    return inner;
  }

  function splitWords(el) {
    const text  = el.innerText.trim();
    const words = text.split(/\s+/);
    el.innerHTML = words.map(word =>
      `<span class="anim-word"><span class="anim-word__inner">${word}</span></span>`
    ).join(' ');
    return el.querySelectorAll('.anim-word__inner');
  }

  // ── SET INIZIALE SINCRONO ────────────────────────────────
  function setInitialStates() {

    document.querySelectorAll([
      '[data-animate="fade"]',
      '[data-animate="slide-up"]',
      '[data-animate="slide-left"]',
      '[data-animate="slide-right"]',
      '[data-animate="scale"]',
    ].join(',')).forEach(el => {
      gsap.set(el, { opacity: 0 });
    });

    document.querySelectorAll('[data-animate="clip-up"]').forEach(el => {
      const styleName = el.dataset.style || 'default';
      const presetY   = CONFIG.clipUp[styleName]?.y || '1.3em';
      const inners    = splitWords(el);
      gsap.set(Array.from(inners), { y: presetY, opacity: 0 });
      el.dataset.animReady = 'true';
    });

    document.querySelectorAll('[data-animate="clip-x"]').forEach(el => {
      const inner = wrapForClip(el);
      gsap.set(inner, { x: '-105%' });
    });

    document.querySelectorAll('[data-animate="clip-y"]').forEach(el => {
      const inner = wrapForClip(el);
      gsap.set(inner, { y: '-105%' });
    });

    document.querySelectorAll('[data-animate="stagger"]').forEach(el => {
      gsap.set(Array.from(el.children), { opacity: 0, y: CONFIG.distance * 0.6 });
    });
  }

  // ── MAPPA ANIMAZIONI ─────────────────────────────────────

  const ANIMATIONS = {

    'fade': el => ({
      targets: [el], from: { opacity: 0 }, to: { opacity: 1 }, single: true,
    }),

    'slide-up': el => ({
      targets: [el],
      from: { opacity: 0, y: opt(el, 'distance', CONFIG.distance) },
      to:   { opacity: 1, y: 0 },
      single: true,
    }),

    'slide-left': el => ({
      targets: [el],
      from: { opacity: 0, x: opt(el, 'distance', CONFIG.distance) },
      to:   { opacity: 1, x: 0 },
      single: true,
    }),

    'slide-right': el => ({
      targets: [el],
      from: { opacity: 0, x: -opt(el, 'distance', CONFIG.distance) },
      to:   { opacity: 1, x: 0 },
      single: true,
    }),

    'scale': el => ({
      targets: [el],
      from: { opacity: 0, scale: 0.92 },
      to:   { opacity: 1, scale: 1 },
      single: true,
    }),

    'clip-x': el => {
      const inner = wrapForClip(el);
      return { targets: [inner], from: { x: '-105%' }, to: { x: '0%' }, single: true };
    },

    'clip-y': el => {
      const inner = wrapForClip(el);
      return { targets: [inner], from: { y: '-105%' }, to: { y: '0%' }, single: true };
    },

    'clip-up': el => {
      const inners    = el.querySelectorAll('.anim-word__inner');
      const styleName = el.dataset.style || 'default';
      const presetY   = CONFIG.clipUp[styleName]?.y || '1.3em';
      return {
        targets: Array.from(inners),
        from:    { y: presetY, opacity: 0 },
        to:      { y: '0em',   opacity: 1 },
        single:  false,
      };
    },

  };

  function initElement(el) {
    const type = el.dataset.animate;
    if (!type || type === 'stagger') return;

    const animFn = ANIMATIONS[type];
    if (!animFn) { console.warn('[animations.js] Tipo non riconosciuto:', type); return; }

    const { targets, from, to, single } = animFn(el);
    if (!targets || !targets.length) return;

    const isClipUp    = type === 'clip-up';
    const styleName   = el.dataset.style || 'default';
    const stylePreset = isClipUp ? (CONFIG.clipUp[styleName] || CONFIG.clipUp.default) : {};

    const duration   = opt(el, 'duration', isClipUp ? stylePreset.duration : CONFIG.duration);
    const delay      = opt(el, 'delay',    0);
    const ease       = opt(el, 'ease',     isClipUp ? stylePreset.ease     : CONFIG.ease);
    const staggerVal = opt(el, 'stagger',  isClipUp ? stylePreset.stagger  : 0);
    const start      = opt(el, 'start',    CONFIG.start);
    const once       = opt(el, 'once',     CONFIG.once);

    const toVars = {
      ...to, duration, delay, ease,
      scrollTrigger: { trigger: el, start, once },
    };

    if (!single && targets.length > 1) {
      toVars.stagger = staggerVal;
      gsap.fromTo(targets, from, toVars);
    } else {
      gsap.fromTo(targets[0], from, toVars);
    }
  }

  function initStagger(el) {
    const children = Array.from(el.children);
    if (!children.length) return;
    gsap.fromTo(
      children,
      { opacity: 0, y: CONFIG.distance * 0.6 },
      {
        opacity: 1, y: 0,
        duration: opt(el, 'duration', CONFIG.duration),
        delay:    opt(el, 'delay',    0),
        ease:     opt(el, 'ease',     CONFIG.ease),
        stagger:  opt(el, 'stagger',  CONFIG.stagger),
        scrollTrigger: { trigger: el, start: opt(el, 'start', CONFIG.start), once: true },
      }
    );
  }

  function init() {
    document.querySelectorAll('[data-animate]:not([data-animate="stagger"])')
      .forEach(initElement);
    document.querySelectorAll('[data-animate="stagger"]')
      .forEach(initStagger);
    requestAnimationFrame(() => ScrollTrigger.refresh());
  }

  // ── ORDINE DI ESECUZIONE ─────────────────────────────────
  // 1. resolveClassAnimations() — traduce classi in data attributes
  // 2. setInitialStates()       — nasconde elementi, sincrono
  // 3. init()                   — avvia animazioni al window.load
  resolveClassAnimations();
  setInitialStates();

  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init);
  }

};

export default animations;

// ─── SISTEMA FADEIN SEMPLICE ──────────────────────────────────
// Aggiunta separata — non interferisce con il sistema data-animate
//
// Classi disponibili:
//   anim-fadein           → fade in alpha, molto soft
//   anim-delay-1          → delay 0.2s
//   anim-delay-2          → delay 0.4s
//   anim-delay-3          → delay 0.6s
//
// Uso in Gutenberg → Avanzate → Classi CSS aggiuntive:
//   anim-fadein
//   anim-fadein anim-delay-1
//   anim-fadein anim-delay-2
//   anim-fadein anim-delay-3

;(function initFadeIn() {

  const FADEIN_CFG = {
    duration: 1.2,              // lunga — effetto molto soft
    ease:     'power2.out',
    start:    'top 90%',        // entra tardi — quasi già in viewport
    delays: {
      'anim-delay-1': 0.2,
      'anim-delay-2': 0.4,
      'anim-delay-3': 0.6,
    },
  };

  function setFadeInInitial() {
    document.querySelectorAll('.anim-fadein').forEach(el => {
      gsap.set(el, { opacity: 0 });
    });
  }

  function initFadeInElements() {
    document.querySelectorAll('.anim-fadein').forEach(el => {

      // Legge delay dalle classi
      let delay = 0;
      Object.entries(FADEIN_CFG.delays).forEach(([cls, val]) => {
        if (el.classList.contains(cls)) delay = val;
      });

      gsap.fromTo(el,
        { opacity: 0 },
        {
          opacity:  1,
          duration: FADEIN_CFG.duration,
          delay,
          ease:     FADEIN_CFG.ease,
          scrollTrigger: {
            trigger: el,
            start:   FADEIN_CFG.start,
            once:    true,
          },
        }
      );
    });
  }

  // Stato iniziale sincrono — evita flash
  setFadeInInitial();

  if (document.readyState === 'complete') {
    initFadeInElements();
  } else {
    window.addEventListener('load', initFadeInElements);
  }

})();